import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { Combobox } from '@/components/ui/combobox';
import type { ListboxOption } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';

const QUERY_DEBOUNCE_MS = 200;

/**
 * The base every async entity picker composes: members, funds, groups.
 *
 * Searching happens on the server, so a church with twelve thousand members is not
 * asked to download all of them to pick one. The currently selected entity is kept
 * separately from the search results, because the search that found it is long gone
 * by the time the form is reopened for editing.
 */

export interface EntityOption {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface EntityComboboxProps {
  value: string | null;
  onChange: (id: string | null) => void;
  /** Results for the current query. */
  options: EntityOption[];
  /** The selected entity, resolved by the caller. Keeps the label after a reload. */
  selected?: EntityOption | null;
  onQueryChange: (query: string) => void;
  loading?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  clearLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  /** "Add a new member" row under the results. */
  createAction?: { label: string; onClick: (query: string) => void };
  popoverPortal?: PopoverPortalTarget;
  'aria-describedby'?: string;
  className?: string;
}

export const EntityCombobox = ({
  value,
  onChange,
  options,
  selected,
  onQueryChange,
  loading = false,
  placeholder,
  emptyMessage,
  clearLabel,
  disabled,
  invalid,
  id,
  createAction,
  popoverPortal = 'body',
  className,
  ...aria
}: EntityComboboxProps) => {
  const [query, setQuery] = useState('');
  const [committedSelected, setCommittedSelected] = useState<EntityOption | null>(null);

  // Debounce the parent search so each keystroke does not remount the option list
  // mid-open (that flash is what makes the giver picker unusable in a dialog).
  useEffect(() => {
    const handle = window.setTimeout(() => onQueryChange(query), QUERY_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!value) setCommittedSelected(null);
  }, [value]);

  const resolvedSelected = useMemo(() => {
    if (!value) return null;
    if (selected?.id === value) return selected;
    if (committedSelected?.id === value) return committedSelected;
    return options.find((option) => option.id === value) ?? null;
  }, [committedSelected, options, selected, value]);

  const listOptions = useMemo<ListboxOption<string>[]>(() => {
    const merged = [...options];
    // The selected entity is pinned into the list when the current results do not
    // include it, so the field can render its label instead of a bare id.
    if (resolvedSelected && !merged.some((option) => option.id === resolvedSelected.id)) {
      merged.unshift(resolvedSelected);
    }
    return merged.map((option) => ({
      value: option.id,
      label: option.label,
      description: option.description,
      icon: option.icon,
      disabled: option.disabled,
    }));
  }, [options, resolvedSelected]);

  const handleChange = (next: string | null) => {
    if (!next) {
      setCommittedSelected(null);
      onChange(null);
      return;
    }
    const match =
      options.find((option) => option.id === next) ??
      (resolvedSelected?.id === next ? resolvedSelected : null);
    if (match) setCommittedSelected(match);
    onChange(next);
  };

  return (
    <Combobox
      value={value}
      onChange={handleChange}
      options={listOptions}
      query={query}
      onQueryChange={setQuery}
      loading={loading}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      clearLabel={clearLabel}
      disabled={disabled}
      invalid={invalid}
      id={id}
      popoverPortal={popoverPortal}
      className={className}
      footer={
        createAction ? (
          <button
            type="button"
            onClick={() => createAction.onClick(query)}
            className="w-full rounded-md px-2 py-2 text-left text-body-sm font-medium text-brand hover:bg-surface-sunken"
          >
            {createAction.label}
          </button>
        ) : undefined
      }
      {...aria}
    />
  );
};
